import { type FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';

import { Button, Text, colors } from '@soluto-private/mx-asurion-ui-react';

import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import { useGoToStep } from '@service-initiation/shared-data-access-flow-manager';
import { CheckCircleIcon } from '@service-initiation/shared-ui-check-circle-icon';
import { ClickableText } from '@service-initiation/shared-ui-clickable-text';
import { HorizontalRule } from '@service-initiation/shared-ui-horizontal-rule';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { Route } from '@service-initiation/tech-care-data-access-routing';
import {
  useCustomerDetailsConfirmedAtom,
  useCustomerDetailsValue,
  useLeadIdValue,
  useMailInValue,
  useSetAlertBannerAtom,
} from '@service-initiation/tech-care-data-access-store';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';

import mailInImg from '../../assets/mail-in-img.png';
import { useCreateMailInLead } from '../../hooks/useCreateMailInLead';

const Container = styled.div<{ show: boolean }>`
  text-align: left;
  margin-bottom: 40px;
  opacity: ${({ show }) => (show ? '1' : '0')};
  transition: opacity 200ms linear;
`;

const TopImage = styled.img`
  width: 100%;
`;

const Callout = styled.div`
  background: ${colors.neutralBrightest};
  border-radius: 8px;
  padding: 1.5em 1em;
`;

const ShippingInstructionsContainer = styled.div`
  padding-top: 1em;
  padding-bottom: 2em;
  padding-left: 2.5em;
  li {
    margin: 10px 0;
  }
`;
const BeforeYouShipInstructionsContainer = styled.div`
  padding-top: 1em;
  padding-bottom: 2em;
  padding-left: 1.5em;
  li {
    margin: 10px 0;
  }
`;
const TEXT = TranslationEntries.Repairs.StoreLocator.MailInConfirmation;
const ERROR_TEXT = TranslationEntries.Repairs.Error;
const handleLinkClick = (link?: string) => {
  window.open(link, '_blank');
};

const ShippingLabelButton = styled(Button)`
  margin-top: 1.5em;
  margin-bottom: 1.5em;
`;

const UList = styled.ul``;
const UListItem = styled.li`
  display: list-item;
  list-style-type: disc;
`;
const OList = styled.ol`
  list-style-type: auto;
`;
const OListItem = styled.li`
  display: list-item;
`;
const upsLocationLink = 'https://locations.ups.com/';
const findMyLink =
  'https://www.asurion.com/connect/tech-tips/how-to-turn-off-find-my-iphone/';
const factoryResetLink =
  'https://www.asurion.com/connect/tech-tips/how-to-use-factory-reset-protection/';
const backupDataLink =
  'https://www.asurion.com/connect/tech-tips/how-to-back-up-your-iphone-and-android-phone/';
const CUSTOMER_DETAILS_PATHNAME = '/tech-care/repairs/customer-details';
export const MailInConfirmationPage: FC = () => {
  const customerDetails = useCustomerDetailsValue();
  const leadId = useLeadIdValue();
  const mailIn = useMailInValue();
  const { t } = useTranslation();
  const { goToStep } = useGoToStep();
  const setAlertBanner = useSetAlertBannerAtom();
  const [topImageLoaded, setTopImageLoaded] = useState<boolean>(false);
  const printShippingLabelLink = mailIn?.shippingLabel?.label_link;
  const { dispatchViewEvent } = useAnalytics();
  const [, setCustomerDetailsConfirmed] = useCustomerDetailsConfirmedAtom();

  const { ref: elementRef } = useInViewAnalytics(
    'MailInConfirmation',
    {
      ...(customerDetails?.phoneNumber && {
        mdn: customerDetails?.phoneNumber,
      }),
      CompletedFlow: true,
      marketingCommunicationsOptIn:
        customerDetails?.agreeToEmailOffers || false,
    },
    {
      Email: customerDetails?.email || '',
    }
  );
  const [
    { error: mailInLeadError, loading: mailInLeadLoading },
    createMailInFn,
  ] = useCreateMailInLead();

  useEffectOnce(() => {
    if (!leadId) {
      createMailInFn();
    }
  });

  useEffect(() => {
    if (mailInLeadError) {
      setAlertBanner({
        bannerType: 'error',
        message: t(ERROR_TEXT.GenericError),
        skipResetOnPaths: [CUSTOMER_DETAILS_PATHNAME],
      });
      dispatchViewEvent('AlertBanner', {
        error: mailInLeadError,
        message: t(ERROR_TEXT.GenericError),
      });
      setCustomerDetailsConfirmed(false);
      goToStep(Route.CustomerDetails, { replace: true });
    }
  }, [
    dispatchViewEvent,
    goToStep,
    mailInLeadError,
    setAlertBanner,
    setCustomerDetailsConfirmed,
    t,
  ]);
  return (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          {!mailInLeadLoading || !topImageLoaded ? (
            <Container
              data-testid="mail-in-confirmation-container"
              ref={elementRef}
              show={topImageLoaded}
            >
              <CheckCircleIcon />
              <StepTitle
                subtitle={t(TEXT.Subtitle)}
                title={
                  <Trans
                    components={{
                      Pii: <span className="fs-mask" />,
                    }}
                    data-testid="confirmation-title"
                    i18nKey={TEXT.Title}
                    values={{ firstName: customerDetails?.firstName }}
                  />
                }
              />
              <TopImage
                alt="confirmation-top-image"
                data-testid="confirmation-top-image"
                onLoad={() => setTopImageLoaded(true)}
                src={mailInImg}
              />
              <ShippingLabelButton
                color="primary"
                data-testid="print-label-confirmation-button"
                onClick={() => handleLinkClick(printShippingLabelLink)}
                variant="default"
              >
                {t(TEXT.PrintShippingLabel)}
              </ShippingLabelButton>
              <Text size={3} weight="base">
                {t(TEXT.ShippingInstructionsTitle)}
              </Text>
              <ShippingInstructionsContainer>
                <Trans
                  components={{
                    LightUnderline: (
                      <ClickableText
                        color={colors.primaryDeep}
                        onClick={() => handleLinkClick(upsLocationLink)}
                      />
                    ),
                    List: <OList />,
                    Text: <OListItem />,
                    b: <Text color="black" weight="heavy" />,
                  }}
                  i18nKey={TEXT.ShippingInstructionsContent}
                />
              </ShippingInstructionsContainer>
              <HorizontalRule style={{ paddingBottom: '2em' }} />

              <Text size={3} style={{ marginBottom: '1em' }} weight="base">
                {t(TEXT.BeforeYouShipTitle)}
              </Text>
              <BeforeYouShipInstructionsContainer>
                <Trans
                  components={{
                    BackUpYourDataUnderline: (
                      <ClickableText
                        color={colors.primaryDeep}
                        onClick={() => handleLinkClick(backupDataLink)}
                      />
                    ),
                    FactoryResetUnderline: (
                      <ClickableText
                        color={colors.primaryDeep}
                        onClick={() => handleLinkClick(factoryResetLink)}
                      />
                    ),
                    FindMyLightUnderline: (
                      <ClickableText
                        color={colors.primaryDeep}
                        onClick={() => handleLinkClick(findMyLink)}
                      />
                    ),
                    List: <UList />,
                    Text: <UListItem />,
                    b: <Text color="black" weight="heavy" />,
                  }}
                  i18nKey={TEXT.BeforeYouShipContent}
                />
              </BeforeYouShipInstructionsContainer>

              <Callout>
                <Text size={3} weight="heavy">
                  {t(TEXT.AfterYouShipTitle)}
                </Text>
                <br />
                <Text size={2} weight="base">
                  {t(TEXT.AfterYouShipContent)}
                </Text>
              </Callout>
            </Container>
          ) : (
            <StepContentSkeleton />
          )}
        </LeftMainContentSection>
      </MainContentContainer>
    </PageContainer>
  );
};
